import type { ChangeEvent, FC } from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineArrowUpTray,
  HiOutlineBuildingOffice2,
  HiOutlineTrash,
} from "react-icons/hi2";

import { Button } from "../Button/Button";
import { useUserSession } from "../../context/UserContext";
import { useCanManageWorkspace } from "../../hooks/useCanManageWorkspace";
import {
  COMPANY_LOGO_ACCEPTED_TYPES,
  CompanyLogoValidationError,
  deleteCompanyLogo,
  uploadCompanyLogo,
} from "../../data/companyLogoStorage";
import type { Company } from "../../models";
import type { ICompany } from "../../types";

interface CompanyLogoEditorProps {
  company: Company;
  isSaving: boolean;
  onSave: (updates: Partial<ICompany>) => Promise<void>;
  onBusyChange?: (busy: boolean) => void;
}

type LogoAction = "uploading" | "removing" | null;

export const CompanyLogoEditor: FC<CompanyLogoEditorProps> = ({
  company,
  isSaving,
  onSave,
  onBusyChange,
}) => {
  const { t } = useTranslation("company");
  const { userSession } = useUserSession();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [action, setAction] = useState<LogoAction>(null);
  const [error, setError] = useState<string | null>(null);
  const canManage = useCanManageWorkspace(company.workspaceId);
  const disabled = Boolean(action) || isSaving;

  const errorMessage = (uploadError: unknown) => {
    if (uploadError instanceof CompanyLogoValidationError) {
      return t(`page.logo.errors.${uploadError.code}`);
    }
    return t("page.logo.errors.upload_failed");
  };

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !canManage) return;

    setAction("uploading");
    onBusyChange?.(true);
    setError(null);
    let uploaded: { logoUrl: string; logoStoragePath: string } | undefined;

    try {
      uploaded = await uploadCompanyLogo({
        file,
        workspaceId: company.workspaceId,
        companyId: company.id,
        uploadedBy: userSession?.user.email || "",
      });
      await onSave(uploaded);

      if (
        company.logoStoragePath &&
        company.logoStoragePath !== uploaded.logoStoragePath
      ) {
        try {
          await deleteCompanyLogo({
            storagePath: company.logoStoragePath,
            workspaceId: company.workspaceId,
            companyId: company.id,
          });
        } catch (cleanupError) {
          console.warn(
            "Unable to remove the previous company logo",
            cleanupError,
          );
        }
      }
    } catch (uploadError) {
      if (uploaded) {
        try {
          await deleteCompanyLogo({
            storagePath: uploaded.logoStoragePath,
            workspaceId: company.workspaceId,
            companyId: company.id,
          });
        } catch (cleanupError) {
          console.warn(
            "Unable to roll back the uploaded company logo",
            cleanupError,
          );
        }
      }
      setError(errorMessage(uploadError));
    } finally {
      setAction(null);
      onBusyChange?.(false);
    }
  };

  const remove = async () => {
    if (!company.logoUrl || !canManage) return;
    setAction("removing");
    onBusyChange?.(true);
    setError(null);
    try {
      await onSave({ logoUrl: null, logoStoragePath: null });
      try {
        await deleteCompanyLogo({
          storagePath: company.logoStoragePath,
          workspaceId: company.workspaceId,
          companyId: company.id,
        });
      } catch (cleanupError) {
        console.warn("Unable to remove the company logo file", cleanupError);
      }
    } catch {
      setError(t("page.logo.errors.remove_failed"));
    } finally {
      setAction(null);
      onBusyChange?.(false);
    }
  };

  return (
    <div>
      <div
        className="grid aspect-square w-full place-items-center overflow-hidden rounded-lg border border-dashed border-border-strong bg-surface"
        data-testid="company-logo-preview"
      >
        {company.logoUrl ? (
          <img
            src={company.logoUrl}
            alt={t("page.logo.preview_alt", { company: company.name })}
            className="h-full w-full object-contain p-4"
            referrerPolicy="no-referrer"
          />
        ) : (
          <HiOutlineBuildingOffice2 className="h-10 w-10 text-muted" />
        )}
      </div>

      {canManage ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={COMPANY_LOGO_ACCEPTED_TYPES.join(",")}
            className="hidden"
            onChange={upload}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineArrowUpTray className="h-4 w-4" />}
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              {action === "uploading"
                ? t("page.logo.uploading")
                : company.logoUrl
                  ? t("page.logo.replace")
                  : t("page.logo.upload")}
            </Button>
            {company.logoUrl && (
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineTrash className="h-4 w-4" />}
                disabled={disabled}
                onClick={() => void remove()}
              >
                {action === "removing"
                  ? t("page.logo.removing")
                  : t("page.logo.remove")}
              </Button>
            )}
          </div>
        </>
      ) : (
        <p className="mt-4 text-xs text-muted">{t("page.logo.read_only")}</p>
      )}

      <p className="mt-3 text-xs leading-5 text-muted">
        {t("page.logo.requirements")}
      </p>
      {error && (
        <p className="mt-2 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
